import Header from '../Header/Header';
import { CustomTable, ConfirmationPopup, SuccessModal, Pagination, Search, Loader } from '@ohif/ui';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '@custom/store/store';
import { useDispatch, useSelector } from 'react-redux';
import fetchTeleraidologyList from '@custom/rtk-apis/teleraidologycustomer/teleraidologyCustomerList';
import { capitalizeWords, customStyle, formatCustomerType, getLocalPhoneNumber } from '@custom/Utils/utils';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _hospitalUrls, _teleradiologyuserUrls } from '@custom/Utils/constants/apiUrls';
import { useTheme } from "../../ui/src/contextProviders"

const Teleradiology = () => {
  const dispatch = useDispatch<AppDispatch>();
  const data = useSelector((state: RootState) => state.teleraidologyCustomerSlice);

  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [isSuccessModalOpen, setIsSuccessModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableData, setTableData] = useState([]);
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [totalPg, setTotalPg] = useState(0);
  const [load, setLoad] = useState(true);
  const [searchInputValue, setSearchInputValue] = useState('');
  const role = sessionStorage.getItem('role');

  useEffect(() => {
    fetchList()
  }, [dispatch, searchQuery, pgLimit, pgNumber]);
  const fetchList = () => {
    dispatch(fetchTeleraidologyList({ limit: pgLimit, pageNumber: pgNumber, search: searchQuery }));
  }

  useEffect(() => {
    if (data.data) {
      const formattedData = data.data.map(item => ({
        ...item,
        name: item?.hospitalName ? capitalizeWords(item.hospitalName) : '--',
        email: item?.hospitalEmailId || '--',
        userName: item?.user?.name ? capitalizeWords(item?.user?.name) : 'N/A',
        contactNumber: `${item?.contactNumberCountryCode || ''} ${item?.contactNumber || ''}`,
        customerType: formatCustomerType(item?.customerType) || '--',
        radiologistNumber: item?.radiologistNumber || '--',
      }));
      setTotalPg(data?.totalRecords)
      setTableData(formattedData);
    }
    setLoad(false);
  }, [data.data]);

  const onPageChangeHandler = pg => {
    setPgNumber(pg);
  };

  const handlePageSizeChange = e => {
    setPgLimit(e);
  };

  const columns = [
    { key: 'name', title: 'Name', sort: true },
    { key: 'customerType', title: 'User Type' },
    { key: 'userName', title: 'Super Admin', sort: true },
    { key: 'email', title: 'Email ID', sort: true },
    { key: 'contactNumber', title: 'Contact Details' },
    { key: 'radiologistNumber', title: 'No. of Radiologists' },
    { key: 'actions', title: 'Action' }
  ];

  const actions = [
    {
      label: 'Edit',
      iconName: 'edit',
      onClick: (item) => {
        const updatedItem = {
          ...item,
          contactNumber: getLocalPhoneNumber(item?.contactNumber)
        };
        navigate("/addteleradiology", {
          state: {
            teleraidologyCustomer: updatedItem,
            isTeleraidologiestAdminEdit: true,
            hospitalId: item?._id
          }
        });
      }
    },
    ...(role !== 'Superadmin' ? [{
      label: 'Delete',
      iconName: 'delete',
      onClick: (item) => {
        setSelectedRow(item);
        setIsPopupOpen(true);
      }
    }] : []),
  ];

  const handleConfirm = async () => {
    try {
      setIsPopupOpen(false);
      const response = await apiRequest({ method: 'POST', endUrl: _teleradiologyuserUrls.delete, headerType: 'json', body: { _id: selectedRow?._id } })
      setTitle(response?.msg);
      if (response.status) {
        setIsSuccessModal(true)
        fetchList()
      }
    }
    catch (error) {
      console.log(error)
    }
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
  };

  const navigateToPage = () => {
    navigate("/addTeleradiologycustomer");
  };

  const searchClick = (searchText: string) => {
    setSearchQuery(searchText);
    setSearchInputValue(searchText);
  };
  const isDarkMode = useTheme();

  return (
    <>
      <div>
        <div className='page page__fullBody' style={
          isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
        }>
          <Header isBack={false} title='Teleradiology Users'></Header>
          <div className='page__body'>
            <div className='tableWrap'>
              <div className='tableWrap__head'>
                <div className="formRow formRow__3 m_0 w_100 formRow--flexEnd">
                  <div className='formCol'>
                    <Search text="Name, Contact Details or Email ID" onSearchClick={searchClick} value={searchInputValue} setValue={setSearchInputValue}></Search>
                  </div>
                </div>
                <div className='btn__group'>
                  <button className="btn btn__white" onClick={navigateToPage}>
                    <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684" stroke="#150823" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    Add Teleradiology Users</button>
                </div>
              </div>
              <div className='tableContainer'>
                {load ? <Loader isLoaderOpen={load} /> : <CustomTable columns={columns} data={tableData} actions={actions} searchQuery={searchQuery} from="teleradiology" userDetails={''} setClickedItem={''} clickedItem={''} />}
              </div>
            </div>
          </div>
          {tableData.length > 0 && <Pagination
            onChangePage={onPageChangeHandler}
            onChangePerPage={handlePageSizeChange}
            totalPage={totalPg}
            currentPage={pgNumber}
            perPage={pgLimit}
          />}
        </div>
      </div>
      <ConfirmationPopup
        isOpen={isPopupOpen}
        message={`Are you sure you want to delete ${selectedRow?.name}?`}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={''}
      />
      <SuccessModal
        isSuccessModalOpen={isSuccessModalOpen}
        title={title}
        handleCloseModal={() => setIsSuccessModal(false)}
      />
      <Loader isLoaderOpen={data?.loading} />
    </>
  );
}

export default Teleradiology;
